// Static gallery first; the existing model viewer is downloaded only after a deliberate click.
let viewerLibrary;
let expandedDialog;
let expandedSequence = 0;
class LsVisualGallery extends HTMLElement {
  connectedCallback() {
    if (this._abort) return;
    this._abort = new AbortController();
    this.stopSpin();
    const spinButton = this.querySelector('[data-spin]');
    if (spinButton) spinButton.hidden = true;
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.stopSpin(); }, { signal: this._abort.signal });
    const rotateButton = this.querySelector('[data-rotate]');
    if (rotateButton) rotateButton.disabled = false;
    const expandButton = this.querySelector('[data-expand]');
    if (expandButton) expandButton.hidden = false;
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.dataset.view) this.selectView(button);
      if (button.hasAttribute('data-rotate')) this.rotate(button);
      if (button.hasAttribute('data-spin')) this.setSpin(button.getAttribute('aria-pressed') !== 'true');
      if (button.hasAttribute('data-expand')) this.expand(button);
    }, { signal: this._abort.signal });
  }
  disconnectedCallback() {
    this.stopSpin();
    this._abort?.abort();
    this._abort = null;
    this._rotationToken = (this._rotationToken || 0) + 1;
    this._expandedDialog?.close();
    this.querySelector('model-viewer')?.remove();
    const link = this.querySelector('[data-main-link]');
    if (link) link.hidden = false;
  }
  async expand(button) {
    const request = ++expandedSequence;
    const library = this.closest('ls-visual-library');
    if (library && !library._readyAll && await library.ensureAll()) library.filter(false);
    if (request !== expandedSequence || !this.isConnected) return;
    expandedDialog?.close();
    this.stopSpin();
    const dialog = document.createElement('dialog');
    dialog.className = 'lsv-view-dialog';
    const heading = document.createElement('h2');
    heading.id = `lsv-expanded-${request}`;
    dialog.setAttribute('aria-labelledby', heading.id);
    const header = document.createElement('div'); header.className = 'lsv-view-dialog__head';
    const close = document.createElement('button');
    close.type = 'button'; close.className = 'lsv-rotate'; close.textContent = this.dataset.closeLabel;
    close.autofocus = true;
    close.addEventListener('click', () => dialog.close());
    header.append(heading, close);
    const stage = document.createElement('div');
    const note = document.createElement('p'); note.className = 'lsv-note';
    const factHeading = document.createElement('p'); factHeading.className = 'lsv-kicker'; factHeading.textContent = this.dataset.printedLabel;
    const facts = document.createElement('dl'); facts.className = 'lsv-inspect-facts';
    const details = document.createElement('a'); details.className = 'lsv-link';
    dialog.append(header, stage);
    const cards = library?._readyAll ? library._groups.flatMap(group => library._cards.filter(card => card._group === group && card._matches)) : [];
    const sources = cards.map(card => card.querySelector('ls-visual-gallery'));
    let index = sources.indexOf(this), previous, next, position;
    const show = source => {
      heading.textContent = source.dataset.model;
      const gallery = source.cloneNode(true);
      gallery.querySelector('[data-expand]')?.remove();
      gallery.querySelectorAll('model-viewer').forEach(viewer => viewer.remove());
      gallery.querySelector('[data-main-link]').hidden = false;
      gallery.querySelector('[data-main]').sizes = '(min-width: 1000px) 900px, 90vw';
      const currentView = gallery.querySelector('[data-main-link]').getAttribute('href');
      for (const view of gallery.querySelectorAll('[data-view]')) view.setAttribute('aria-pressed', String(view.dataset.view === currentView));
      gallery.querySelector('[data-status]').textContent = '';
      stage.replaceChildren(gallery);
      const printed = source.closest('.lsv-card, .lsv-product')?.querySelector('dl');
      facts.replaceChildren(...[...(printed?.children || [])].map(row => row.cloneNode(true)));
      facts.hidden = factHeading.hidden = !printed;
      note.textContent = source.dataset.note;
      details.hidden = !library || !source.dataset.productUrl;
      details.href = source.dataset.productUrl || '#'; details.textContent = source.dataset.productLabel;
      if (position) {
        position.textContent = source.dataset.model + ' \u00b7 ' + this.dataset.positionTemplate.replace('__INDEX__', String(index + 1)).replace('__COUNT__', String(sources.length));
        previous.disabled = index === 0; next.disabled = index === sources.length - 1;
      }
    };
    if (index >= 0 && sources.length > 1) {
      const navigation = document.createElement('nav'); navigation.className = 'lsv-inspect-navigation';
      navigation.setAttribute('aria-label', this.dataset.browseLabel);
      position = document.createElement('p'); position.setAttribute('role', 'status'); position.setAttribute('aria-live', 'polite');
      const navigate = delta => { index += delta; show(sources[index]); };
      previous = document.createElement('button'); previous.type = 'button'; previous.className = 'lsv-rotate';
      previous.dataset.inspectPrevious = ''; previous.textContent = this.dataset.previousLabel;
      previous.addEventListener('click', () => navigate(-1));
      next = document.createElement('button'); next.type = 'button'; next.className = 'lsv-rotate';
      next.dataset.inspectNext = ''; next.textContent = this.dataset.nextLabel;
      next.addEventListener('click', () => navigate(1));
      navigation.append(position, previous, next); dialog.append(navigation);
    }
    dialog.append(factHeading, facts, note, details); show(this);
    dialog.addEventListener('close', () => {
      dialog.remove();
      if (expandedDialog === dialog) expandedDialog = null;
      if (this._expandedDialog === dialog) this._expandedDialog = null;
      if (button.isConnected) button.focus({ preventScroll: true });
    }, { once: true });
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('a[href],button:not(:disabled),[tabindex]:not([tabindex="-1"])')]
        .filter(element => element.getClientRects().length && !element.closest('[hidden]'));
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault(); first?.focus();
      }
    });
    document.body.append(dialog);
    this._expandedDialog = expandedDialog = dialog;
    dialog.showModal();
    close.focus({ preventScroll: true });
  }
  stopSpin() {
    this.querySelector('model-viewer')?.removeAttribute('auto-rotate');
    this.querySelector('[data-spin]')?.setAttribute('aria-pressed', 'false');
  }
  setSpin(enabled) {
    const viewer = this.querySelector('model-viewer');
    if (!viewer?.loaded) return;
    if (enabled) for (const gallery of document.querySelectorAll('ls-visual-gallery')) if (gallery !== this) gallery.stopSpin?.();
    viewer.toggleAttribute('auto-rotate', enabled);
    this.querySelector('[data-spin]')?.setAttribute('aria-pressed', String(enabled));
  }
  selectView(button) {
    this.stopSpin();
    const spinButton = this.querySelector('[data-spin]');
    if (spinButton) spinButton.hidden = true;
    this._rotationToken = (this._rotationToken || 0) + 1;
    this.querySelector('model-viewer')?.remove();
    const link = this.querySelector('[data-main-link]');
    const img = this.querySelector('[data-main]');
    link.hidden = false;
    link.href = button.dataset.view;
    img.srcset = [360, 600, 900, 1500].map(w => `${button.dataset.view}&width=${w} ${w}w`).join(', ');
    img.src = `${button.dataset.view}&width=900`;
    img.alt = `${this.dataset.model} — ${button.getAttribute('aria-label')}`;
    for (const b of this.querySelectorAll('[data-view]')) b.setAttribute('aria-pressed', String(b === button));
    this.querySelector('[data-status]').textContent = button.getAttribute('aria-label');
  }
  async rotate(button) {
    if (!this.dataset.modelUrl || this.querySelector('model-viewer')) return;
    const token = this._rotationToken = (this._rotationToken || 0) + 1;
    const status = this.querySelector('[data-status]');
    button.disabled = true;
    status.textContent = this.dataset.loading;
    try {
      viewerLibrary ??= import(this.dataset.viewerLib).catch(error => { viewerLibrary = null; throw error; });
      await viewerLibrary;
      await customElements.whenDefined('model-viewer');
      if (!this.isConnected || token !== this._rotationToken) return;
      const viewer = document.createElement('model-viewer');
      viewer.setAttribute('camera-controls', '');
      viewer.setAttribute('auto-rotate-delay', '0');
      viewer.setAttribute('rotation-per-second', '20deg');
      viewer.setAttribute('interaction-prompt', 'none');
      viewer.setAttribute('touch-action', 'pan-y');
      viewer.setAttribute('shadow-intensity', '1');
      viewer.setAttribute('environment-image', 'neutral');
      viewer.setAttribute('alt', this.dataset.model);
      viewer.setAttribute('src', this.dataset.modelUrl);
      viewer.addEventListener('load', () => {
        status.textContent = '';
        const spinButton = this.querySelector('[data-spin]');
        if (spinButton) spinButton.hidden = false;
      }, { once: true });
      viewer.addEventListener('error', () => {
        this.stopSpin(); viewer.remove();
        const spinButton = this.querySelector('[data-spin]');
        if (spinButton) spinButton.hidden = true;
        this.querySelector('[data-main-link]').hidden = false; status.textContent = this.dataset.error;
      }, { once: true });
      this.querySelector('[data-stage]').append(viewer);
      this.querySelector('[data-main-link]').hidden = true;
      for (const b of this.querySelectorAll('[data-view]')) b.setAttribute('aria-pressed', 'false');
    } catch {
      status.textContent = this.dataset.error;
    } finally {
      if (this.isConnected) button.disabled = false;
    }
  }
}
if (!customElements.get('ls-visual-gallery')) customElements.define('ls-visual-gallery', LsVisualGallery);

class LsVisualLibrary extends HTMLElement {
  connectedCallback() {
    if (this._abort) return;
    this._abort = new AbortController();
    const options = { signal: this._abort.signal };
    this.addEventListener('toggle', event => {
      if (!event.target.matches?.('[data-film-archive]')) return;
      if (event.target.open) {
        const template = event.target.querySelector('template[data-film-template]');
        if (template?.content.firstElementChild) event.target.append(template.content);
      } else for (const video of event.target.querySelectorAll('video')) video.pause();
    }, { ...options, capture: true });
    this.addEventListener('click', event => {
      const chapter = event.target.closest('[data-film-seek]');
      if (!chapter || event.button > 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const video = chapter.closest('.lsv-film')?.querySelector('video');
      const seconds = Number(chapter.dataset.filmSeek);
      if (!video || !Number.isFinite(seconds) || seconds < 0) return;
      event.preventDefault();
      for (const other of this.querySelectorAll('video')) if (other !== video) other.pause();
      const request = video._chapterRequest = (video._chapterRequest || 0) + 1;
      const seek = () => {
        if (!video.isConnected || video._chapterRequest !== request) return;
        video.currentTime = Math.min(seconds, Number.isFinite(video.duration) ? Math.max(0,video.duration-.1) : seconds);
      };
      if (video.readyState >= 1) seek();
      else video.addEventListener('loadedmetadata', seek, { once: true, signal: this._abort.signal });
      video.play().catch(() => { if (video.isConnected && video._chapterRequest === request) video.focus({ preventScroll: true }); });
      video.scrollIntoView({ block: 'nearest' });
    }, options);
    this.addEventListener('timeupdate', event => {
      const video = event.target;
      if (!video.matches?.('video')) return;
      const film = video.closest('.lsv-film');
      if (!film) return;
      const chapters = [...film.querySelectorAll('[data-film-seek]')];
      const active = chapters.findLast(chapter => Number(chapter.dataset.filmSeek) <= video.currentTime);
      for (const chapter of chapters) chapter.setAttribute('aria-current', String(chapter === active));
    }, { ...options, capture: true });
    this._search = this.querySelector('[data-search-input]');
    this._category = this.querySelector('select[data-category]');
    this._basis = this.querySelector('select[data-basis]');
    this._groups = [...this.querySelectorAll('[data-group]')];
    const anchors = [...this.querySelectorAll('template[data-card-anchor]')];
    this._cards = anchors.length ? anchors.map(anchor => {
      const card = anchor.content.querySelector('.lsv-card') || anchor.nextElementSibling;
      card._anchor = anchor;
      card._group = anchor.closest('[data-group]');
      return card;
    }) : [...this.querySelectorAll('.lsv-card')];
    for (const card of this._cards) card._group ||= card.closest('[data-group]');
    this._knownModels = new Set(this.dataset.models?.split(',').filter(Boolean) || this._cards.map(card => card.dataset.model));
    this._total = this._knownModels.size;
    this._readyAll = !this.dataset.catalogUrl || this._cards.length === this._total;
    this._cardSeed = this._cards[0]?.cloneNode(true);
    this._limits = new Map(this._groups.map(group => [group, 6]));
    if (!this._search || !this._category) return;
    this._categoryOptions = [...this._category.options].map(option => option.cloneNode(true));
    const params = new URL(location.href).searchParams;
    const groupKey = params.get('visual_group') || '';
    this._groupKey = this._groups.some(group => group.dataset.groupKey === groupKey) ? groupKey : '';
    this._search.value = params.get('visual_q') || '';
    const category = params.get('visual_category') || '';
    if ([...this._category.options].some(o => o.value === category)) this._category.value = category;
    const basis = params.get('visual_basis') || '';
    if (this._basis && [...this._basis.options].some(o => o.value === basis)) this._basis.value = basis;
    this.querySelector('[data-filters]').hidden = false;
    this._search.addEventListener('input', () => {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this.refresh(true), 180);
    }, options);
    this._category.addEventListener('change', () => this.refresh(true), options);
    this._basis?.addEventListener('change', () => this.refresh(true), options);
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-group-filter]');
      if (!button) return;
      this._groupKey = button.dataset.groupFilter;
      this.syncGroupButtons();
      this.refresh(true);
    }, options);
    this.querySelector('[data-reset]').addEventListener('click', () => {
      this.reset(); this.filter(true); this._search.focus();
    }, options);
    window.addEventListener('hashchange', async () => {
      let id;
      try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
      if (!this.findTarget(id) && id.startsWith('visual-ls-') && !await this.ensureAll()) return;
      const target = this.findTarget(id);
      if (target && (target.hidden || !target.isConnected || target.closest('[hidden]'))) {
        this.reset(); this.expandTarget(target); this.filter(true);
        target.scrollIntoView();
      }
    }, options);
    this.addEventListener('click', async event => {
      const button = event.target.closest('[data-more]');
      if (!button) return;
      if (!await this.ensureAll()) return;
      const group = button.closest('[data-group]');
      const before = [...group.querySelectorAll('.lsv-card')].filter(card => !card.hidden).length;
      this._limits.set(group, this._limits.get(group) + 12);
      this.filter(false);
      const cards = [...group.querySelectorAll('.lsv-card')].filter(card => !card.hidden);
      cards[before]?.querySelector('h3 a')?.focus({ preventScroll: true });
    }, options);
    this.setupComparison(options, params);
    this.filter(false);
    this.restoreTarget();
  }
  async restoreTarget() {
    let id = '';
    try { id = decodeURIComponent(location.hash.slice(1)); } catch { /* malformed hash */ }
    if ((this._search.value || this._category.value || this._basis?.value || this._groupKey || [...this._selection].some(model => !this._cards.some(card => card.dataset.model === model))
      || (id.startsWith('visual-ls-') && !this.findTarget(id))) && !await this.ensureAll()) return;
    try {
      const target = this.findTarget(decodeURIComponent(location.hash.slice(1)));
      if (target?.classList.contains('lsv-card')) { this.reset(); this.expandTarget(target); }
    } catch { /* malformed hash */ }
    this.filter(false);
    try {
      const target = this.findTarget(decodeURIComponent(location.hash.slice(1)));
      if (target?.classList.contains('lsv-card')) requestAnimationFrame(() => target.scrollIntoView());
    } catch { /* malformed hash */ }
  }
  async refresh(updateUrl) {
    this.resetLimits();
    if ((this._search.value.trim() || this._category.value || this._basis?.value || this._groupKey) && !await this.ensureAll()) return;
    this.filter(updateUrl);
  }
  async ensureAll() {
    if (this._readyAll) return true;
    if (this._loadPromise) return this._loadPromise;
    this.querySelector('[data-results]').textContent = this.dataset.catalogLoading;
    this.querySelector('[data-empty]').hidden = true;
    this.querySelector('[data-catalog-fallback]').hidden = true;
    this._loadPromise = (async () => {
      const response = await fetch(this.dataset.catalogUrl, { credentials: 'omit', signal: this._abort.signal });
      if (!response.ok) throw new Error('Catalog response');
      const data = await response.json();
      const models = new Set(data.products?.map(item => item.model));
      const groups = new Map(this._groups.map(group => [group.dataset.groupKey, group]));
      if (!Array.isArray(data.products) || data.products.length !== this._total || models.size !== this._total
        || [...models].some(model => !this._knownModels.has(model))
        || data.products.some(item => !groups.has(item.group) || item.views?.length !== 3 || !Array.isArray(item.facts))) throw new Error('Catalog edition');
      if (!this.isConnected) return false;
      const present = new Set(this._cards.map(card => card.dataset.model));
      for (const item of data.products) {
        if (present.has(item.model)) continue;
        const group = groups.get(item.group);
        const anchor = document.createElement('template'); anchor.dataset.cardAnchor = '';
        group.querySelector('.lsv-grid').append(anchor);
        // Adopt into an inert template document before assigning unique image URLs.
        const card = this._cardSeed.cloneNode(true); anchor.content.append(card);
        this.fillCard(card, item); card._anchor = anchor; card._group = group;
        this._cards.push(card);
      }
      this._readyAll = true; this.syncComparison(false);
      return true;
    })().catch(() => {
      if (this.isConnected) {
        this.querySelector('[data-results]').textContent = this.dataset.catalogError;
        this.querySelector('[data-catalog-fallback]').hidden = false;
        this.querySelector('[data-product-index]').open = true;
      }
      return false;
    }).finally(() => { this._loadPromise = null; });
    return this._loadPromise;
  }
  fillCard(card, item) {
    card.querySelectorAll('noscript').forEach(element => element.remove());
    card.id = 'visual-' + item.model.toLowerCase();
    card.dataset.model = item.model; card.dataset.category = item.category;
    card.dataset.basis = item.basis;
    card.dataset.search = `${item.model} ${item.category} ${item.facts.flat().join(' ')}`;
    card.hidden = true;
    const gallery = card.querySelector('ls-visual-gallery');
    Object.assign(gallery.dataset, { model: item.model, modelUrl: item.model_url || '', note: item.note, productUrl: item.url });
    const link = gallery.querySelector('[data-main-link]');
    link.href = item.views[0]; link.hidden = false;
    link.setAttribute('aria-label', this.dataset.openTemplate.replace('__MODEL__', item.model));
    const image = gallery.querySelector('[data-main]');
    image.src = item.views[0] + '&width=900';
    image.srcset = [360, 600, 900, 1500].map(width => `${item.views[0]}&width=${width} ${width}w`).join(', ');
    image.alt = `${item.model} — ${item.category}, ${this.dataset.viewLabel} 1`;
    gallery.querySelectorAll('[data-view]').forEach((button, index) => {
      button.dataset.view = item.views[index]; button.setAttribute('aria-pressed', String(index === 0));
      button.querySelector('img').src = item.views[index] + '&width=160';
    });
    gallery.querySelector('.lsv-gallery__badge').textContent = this.dataset[item.basis + 'Label'];
    gallery.querySelector('[data-status]').textContent = '';
    gallery.querySelector('[data-rotate]').hidden = !item.model_url;
    gallery.querySelector('[data-expand]').setAttribute('aria-label', this.dataset.enlargeTemplate.replace('__MODEL__', item.model));
    card.querySelector('.lsv-card__body > .lsv-kicker').textContent = item.category;
    const title = card.querySelector('h3 a'); title.textContent = item.model; title.href = item.url;
    const facts = card.querySelector('dl'); facts.replaceChildren();
    for (const [key, value] of item.facts) {
      const row = document.createElement('div'), label = document.createElement('dt'), text = document.createElement('dd');
      label.textContent = key; text.textContent = value; row.append(label, text); facts.append(row);
    }
    card.querySelector('.lsv-card__body > .lsv-note').textContent = item.note;
    card.querySelector('.lsv-card__body > .lsv-link').href = item.url;
    card.querySelector('[data-compare-control]').hidden = false;
    const checkbox = card.querySelector('[data-compare-check]');
    checkbox.checked = false; checkbox.disabled = false;
    checkbox.setAttribute('aria-label', this.dataset.compareTemplate.replace('__MODEL__', item.model));
  }
  findTarget(id) { return this._cards.find(card => card.id === id) || document.getElementById(id); }
  syncGroupButtons() {
    for (const button of this.querySelectorAll('[data-group-filter]')) {
      button.setAttribute('aria-pressed', String(button.dataset.groupFilter === this._groupKey));
    }
  }
  syncGroupCategories() {
    this.syncGroupButtons();
    if (this._categoryGroup === this._groupKey || (this._groupKey && !this._readyAll)) return;
    const selected = this._category.value;
    const allowed = this._groupKey ? new Set(this._cards.filter(card => card._group.dataset.groupKey === this._groupKey).map(card => card.dataset.category)) : null;
    this._category.replaceChildren(...this._categoryOptions.filter(option => !option.value || !allowed || allowed.has(option.value)).map(option => option.cloneNode(true)));
    this._category.value = !allowed || allowed.has(selected) ? selected : '';
    this._categoryGroup = this._groupKey;
  }
  resetLimits() { for (const group of this._groups) this._limits.set(group, 6); }
  expandTarget(target) {
    if (!target?.classList.contains('lsv-card')) return;
    this._limits.set(target._group, this._cards.length);
  }
  reset() {
    clearTimeout(this._timer);
    this._search.value = ''; this._category.value = '';
    if (this._basis) this._basis.value = '';
    this._groupKey = '';
    this.resetLimits();
  }
  filter(updateUrl) {
    this.syncGroupCategories();
    const normalize = s => s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    const terms = normalize(this._search.value.trim()).split(/\s+/).filter(Boolean);
    const active = terms.length > 0 || Boolean(this._category.value || this._basis?.value || this._groupKey);
    let count = 0;
    for (const card of this._cards) {
      const match = (!this._category.value || card.dataset.category === this._category.value)
        && (!this._groupKey || card._group.dataset.groupKey === this._groupKey)
        && (!this._basis?.value || card.dataset.basis === this._basis.value)
        && terms.every(term => normalize(card.dataset.search).includes(term));
      card._matches = match;
      card.hidden = !match;
      if (match) count++;
    }
    for (const group of this._groups) {
      const matching = this._cards.filter(card => card._group === group && !card.hidden);
      const matchCount = !this._readyAll && !active ? Number(group.dataset.total) : matching.length;
      group.hidden = matchCount === 0;
      const limit = this._limits.get(group);
      matching.forEach((card, index) => { card.hidden = index >= limit; });
      const more = group.querySelector('[data-more]');
      more.hidden = matchCount <= limit;
      more.textContent = this.dataset.moreTemplate.replace('__COUNT__', String(Math.max(0, matchCount - limit)));
      for (const films of group.querySelectorAll('[data-films]')) films.hidden = active;
      if (group.hidden || active) for (const video of group.querySelectorAll('video')) video.pause();
    }
    // Keep unrevealed galleries inert. Template content is searchable/comparable
    // without registering viewers, fetching thumbnails or adding to the active DOM.
    for (const card of this._cards) {
      if (!card._anchor) continue;
      if (card.hidden && card.isConnected) card._anchor.content.append(card);
      else if (!card.hidden && !card.isConnected) card._anchor.after(card);
    }
    if (!this._readyAll && !active) count = this._total;
    this.querySelector('[data-catalog-fallback]').hidden = true;
    this.querySelector('[data-results]').textContent = this.dataset.resultTemplate.replace('__COUNT__', String(count));
    this.querySelector('[data-empty]').hidden = count > 0;
    if (updateUrl) {
      const url = new URL(location.href);
      for (const [key, value] of [['visual_q', this._search.value.trim()], ['visual_category', this._category.value], ['visual_basis', this._basis?.value], ['visual_group', this._groupKey]]) {
        if (value) url.searchParams.set(key, value); else url.searchParams.delete(key);
      }
      let target;
      try { target = this.findTarget(decodeURIComponent(url.hash.slice(1))); } catch { /* malformed hash */ }
      if (target && (!target.isConnected || target.hidden || target.closest('[hidden]'))) url.hash = '';
      history.replaceState(history.state, '', url);
    }
  }
  setupComparison(options, params) {
    this._selection = new Set((params.get('visual_compare') || '').split(',').filter(model => this._knownModels.has(model)).slice(0, 2));
    this._compareEmpty = this.querySelector('[data-compare-status]').textContent;
    for (const card of this._cards) {
      card.querySelector('[data-compare-control]').hidden = false;
    }
    this.addEventListener('change', event => {
      const checkbox = event.target.closest('[data-compare-check]');
      if (checkbox) {
        const card = checkbox.closest('.lsv-card');
        if (checkbox.checked && this._selection.size < 2) this._selection.add(card.dataset.model);
        else this._selection.delete(card.dataset.model);
        this.syncComparison(true);
      }
    }, options);
    this.querySelector('[data-clear-compare]').addEventListener('click', () => {
      this._selection.clear(); this.syncComparison(true);
    }, options);
    this.querySelector('[data-open-compare]').addEventListener('click', () => this.openComparison(), options);
    this.querySelector('[data-close-compare]').addEventListener('click', () => this.querySelector('dialog').close(), options);
    this.syncComparison(false);
  }
  syncComparison(updateUrl) {
    for (const card of this._cards) {
      const checkbox = card.querySelector('[data-compare-check]');
      checkbox.checked = this._selection.has(card.dataset.model);
      checkbox.disabled = !checkbox.checked && this._selection.size >= 2;
    }
    this.querySelector('[data-compare-status]').textContent = this._selection.size ? [...this._selection].join(' + ') : this._compareEmpty;
    this.querySelector('[data-open-compare]').disabled = this._selection.size !== 2 || [...this._selection].some(model => !this._cards.some(card => card.dataset.model === model));
    this.querySelector('[data-clear-compare]').disabled = this._selection.size === 0;
    if (updateUrl) {
      const url = new URL(location.href);
      if (this._selection.size) url.searchParams.set('visual_compare', [...this._selection].join(','));
      else url.searchParams.delete('visual_compare');
      history.replaceState(history.state, '', url);
    }
  }
  openComparison() {
    if (this._selection.size !== 2) return;
    const cards = [...this._selection].map(model => this._cards.find(card => card.dataset.model === model));
    const facts = cards.map(card => new Map([...card.querySelectorAll('dl > div')].map(row => [row.querySelector('dt').textContent, row.querySelector('dd').textContent])));
    const make = (tag, text) => { const element = document.createElement(tag); if (text != null) element.textContent = text; return element; };
    const table = make('table'); const head = make('thead'); const row = make('tr');
    const heading = make('th', this.dataset.factLabel); heading.scope = 'col'; row.append(heading);
    for (const card of cards) {
      const cell = make('th'); cell.scope = 'col';
      const image = make('img'); const source = card.querySelector('[data-main]');
      image.src = source.src; image.alt = card.dataset.model; image.width = 240; image.height = 240;
      const link = make('a', card.dataset.model); link.href = card.querySelector('h3 a').href;
      cell.append(image, link, make('p', card.querySelector('.lsv-gallery__badge').textContent)); row.append(cell);
    }
    head.append(row); table.append(head); const body = make('tbody');
    for (const key of new Set(facts.flatMap(map => [...map.keys()]))) {
      const row = make('tr'); const label = make('th', key); label.scope = 'row'; row.append(label);
      for (const map of facts) row.append(make('td', map.get(key) || '—'));
      body.append(row);
    }
    table.append(body);
    const content = this.querySelector('[data-compare-content]'); content.replaceChildren(table);
    const notes = make('div'); notes.className = 'lsv-compare-notes';
    for (const card of cards) notes.append(make('p', `${card.dataset.model}: ${card.querySelector('.lsv-card__body > .lsv-note').textContent}`));
    content.append(notes); this.querySelector('dialog').showModal();
  }
  disconnectedCallback() {
    clearTimeout(this._timer);
    this.querySelector('dialog[open]')?.close();
    this._abort?.abort(); this._abort = null;
  }
}
if (!customElements.get('ls-visual-library')) customElements.define('ls-visual-library', LsVisualLibrary);
